import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import
{
    $getNodeByKey, $getSelection, $isNodeSelection,
    NodeKey, BaseSelection,
    CLICK_COMMAND, COMMAND_PRIORITY_LOW, KEY_BACKSPACE_COMMAND, KEY_DELETE_COMMAND
}
from 'lexical';
import { mergeRegister } from '@lexical/utils';
import {useLexicalNodeSelection} from '@lexical/react/useLexicalNodeSelection';
import Resizer from "./Resizer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import * as Dim2 from '../../../lib/Graphics/Dim2';
import { $isImageNode } from "../nodes/InlineImageNode";

export type ImagePosition = 'left' | 'right' | 'full' | undefined;

export type ImageProperties =
{
    src: string
    width?: number
    height?: number
    position?: ImagePosition
    alt?: string
    title?: string
    attrs?: Record<string, string>
}

export function Image({ nodeKey, properties }: { nodeKey: NodeKey, properties?: ImageProperties })
{
    const initialSize = (properties?.width && properties?.height) ? { width: properties.width, height: properties.height } : null;
    const ref = useRef<HTMLImageElement>(null);
    const [isSelected, setIsSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
    const [selection, setSelection] = useState<BaseSelection | null>(null);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [size, setSize] = useState<Dim2.Size | null>(initialSize);
    const [editor] = useLexicalComposerContext();
    const editorRootElement = editor.getRootElement();
    const maxWidth = (editorRootElement?.getBoundingClientRect().width ?? 120) - 20;

    const handleDelete = useCallback((event: KeyboardEvent) =>
    {
        return false;
    },
    []);

    const handleResizeEnd = (width: number, height: number) =>
    {
        setTimeout(() => { setIsResizing(false); }, 200);
        editor.update(() =>
        {
            const node = $getNodeByKey(nodeKey);
            if ($isImageNode(node)) { node.setSize(width, height); }
        });
    };

    const handleResizeStart = () =>
    {
        setIsResizing(true);
    };

    const onImageLoad = useCallback(() =>
    {
        if (ref.current && !size)
        {
            let { naturalWidth, naturalHeight } = ref.current;
            if (naturalWidth > maxWidth)
            {
                const ratio = maxWidth / naturalWidth;
                naturalWidth = maxWidth;
                naturalHeight = Math.round(naturalHeight * ratio);
            }
            setSize({ width: naturalWidth, height: naturalHeight });
        }
        return false;
    },
    []);

    useEffect(() =>
    {
        const editorUpdate = editor.registerUpdateListener(({ editorState }) => { setSelection(editorState.read(() => $getSelection())); });
        const click = editor.registerCommand<MouseEvent>
        (
            CLICK_COMMAND,
            event =>
            {
                if (isResizing) { return true; }
                // console.log('mouseClick', event.target === ref.current);
                if (event.target === ref.current)
                {
                    if (!event.shiftKey) { clearSelection(); }
                    setIsSelected(!isSelected);
                    return true;
                }
                return false;
            },
            COMMAND_PRIORITY_LOW,
        );
        const keyDelete = editor.registerCommand(KEY_DELETE_COMMAND, handleDelete, COMMAND_PRIORITY_LOW);
        const keyBackspace = editor.registerCommand(KEY_BACKSPACE_COMMAND, handleDelete, COMMAND_PRIORITY_LOW);
        return mergeRegister(editorUpdate, click, keyDelete, keyBackspace);
    },
    []);

    const altText = properties?.alt ?? '';
    const src = properties?.src;
    const style = { height: size?.height, width: size?.width };

    const draggable = isSelected && $isNodeSelection(selection);
    const isFocused = $isNodeSelection(selection) && (isSelected || isResizing);
    const element =
    <Suspense fallback={null}>
        <>
            <div draggable={draggable}>
                <img className={isFocused ? 'focused' : ''} src={src} alt={altText} ref={ref} style={style} onLoad={onImageLoad} draggable="false" />
            </div>
            {
                isFocused &&
                <Resizer
                    showCaption={false}
                    setShowCaption={() => {}}
                    editor={editor}
                    targetRef={ref}
                    maxWidth={2000}
                    onResizeStart={handleResizeStart}
                    onResizeEnd={handleResizeEnd}
                />
            }
        </>
    </Suspense>;
    return element;
}